/**
 * Copyright (c) 2017-present, Tomaruba, Inc.
 * All rights reserved.
 */
import { Record } from 'immutable'
import Price from '../../domain/type/Price'
import Quantity from '../../domain/type/Quantity'
import TaxCalculator from '../../domain/model/tax/AccommodationTax'

const _AccommodationTaxState = {
  id: null,
  accommodation_fee_include_tax: 0,
  accommodation_fee: 0,
  booking_price: 0,
  ota_commission_include: false,
  ota_commission_rate: 0,
  tax_per_day: 0,
  number_of_guests: 0,
  number_of_nights: 0,
  payment_commission: 0,
  payment_code: '',
  room_name: '',
  reserved_name: '',
  check_out: null,
  memo: '',
  created_at: '',
  updated_at: ''
}

class AccommodationTaxState extends Record(_AccommodationTaxState) {
  // 宿泊税合計金額
  calculate() {
    return this.tax_per_day * this.number_of_nights * this.number_of_guests
  }

  calculatePerPersonAndDay() {
    return this.tax_per_day
  }

  calculatePerPerson() {
    return this.tax_per_day * this.number_of_nights
  }

  buildBy(reservation) {
    const number_of_adult = reservation.num_adult || 0
    const number_of_child = reservation.num_child || 0

    const number_of_nights = reservation.calculateStayingDays()
    const number_of_guests = number_of_adult + number_of_child
    const commission_setting = TaxCalculator.getCommissionRate(reservation.site_referer)
    const accommodation_fee = TaxCalculator.calculateTotalPrice(reservation.accommodation_fee, commission_setting.include, commission_setting.rate)
    const tax_per_day = TaxCalculator.calculateAccomodationTax(accommodation_fee, number_of_nights, number_of_guests)

    return this.merge({
      accommodation_fee_include_tax: reservation.accommodation_fee,
      number_of_nights: number_of_nights,
      number_of_guests: number_of_guests,
      ota_commission_include: commission_setting.include,
      ota_commission_rate: commission_setting.rate,
      payment_commission: reservation.commission,
      tax_per_day: tax_per_day
    })
  }

  updateBy(reservation, name, value) {
    if (name === 'ota_commission_rate') {
      return this.updateCommissionRate(reservation, new Quantity(value).val())
    }
    if (name === 'payment_commission') {
      let tmp_value = new Price(value).val()
      const payment_commission = tmp_value > 0 ? tmp_value : 0
      return this.updatePaymentCommission(reservation, payment_commission)
    }
    if (name === 'ota_commission_include') {
      return this.updateCommissionInclude(reservation, value)
    }

    let accommodation_fee_include_tax = this.accommodation_fee_include_tax
    if (name === 'accommodation_fee_include_tax') {
      accommodation_fee_include_tax = new Price(value).val()
    }

    let number_of_nights = this.number_of_nights
    if (name === 'number_of_nights') {
      number_of_nights = new Quantity(value).val()
    }

    let number_of_guests = this.number_of_guests
    if (name === 'number_of_guests') {
      number_of_guests = new Quantity(value).val()
    }

    return this.merge({
      accommodation_fee_include_tax: accommodation_fee_include_tax,
      number_of_nights: number_of_nights,
      number_of_guests: number_of_guests,
      tax_per_day: TaxCalculator.calculateAccomodationTax(accommodation_fee_include_tax, number_of_nights, number_of_guests)
    })
  }

  updateCommissionRate(reservation, ota_commission_rate) {
    let commission_rate = ota_commission_rate
    if (commission_rate > 100) {
      commission_rate = 100
    } else if (commission_rate < 0) {
      commission_rate = 0
    }

    const accommodation_fee = TaxCalculator.calculateTotalPrice(this.accommodation_fee_include_tax, this.ota_commission_include, commission_rate)
    const payment_commission = Math.floor(accommodation_fee * (commission_rate / 100))

    return this.merge({
      accommodation_fee_include_tax: accommodation_fee,
      ota_commission_rate: commission_rate,
      payment_commission: payment_commission,
      tax_per_day: TaxCalculator.calculateAccomodationTax(accommodation_fee, this.number_of_nights, this.number_of_guests)
    })
  }

  updatePaymentCommission(reservation, payment_commission) {
    const ota_commission_rate = Math.floor((payment_commission / this.accommodation_fee_include_tax) * 100)
    const accommodation_fee = TaxCalculator.calculateTotalPrice(this.accommodation_fee_include_tax, this.ota_commission_include, ota_commission_rate)

    return this.merge({
      accommodation_fee_include_tax: accommodation_fee,
      ota_commission_rate: ota_commission_rate,
      payment_commission: payment_commission,
      tax_per_day: TaxCalculator.calculateAccomodationTax(accommodation_fee, this.number_of_nights, this.number_of_guests)
    })
  }

  updateCommissionInclude(reservation, ota_commission_include) {
    const ota_commission_rate = this.ota_commission_rate || 0
    const accommodation_fee = TaxCalculator.calculateTotalPrice(reservation.accommodation_fee, ota_commission_include, ota_commission_rate)

    return this.merge({
      accommodation_fee_include_tax: accommodation_fee,
      tax_per_day: TaxCalculator.calculateAccomodationTax(accommodation_fee, this.number_of_nights, this.number_of_guests)
    })
  }
}

export { AccommodationTaxState }
